import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin } from 'rxjs';
import { ListTableComponent } from '@ppl/ui';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { NotificationService } from '../../../../shared/services/notification.service';
import { UserService } from '../../../../services/user.service';
import { correctionResults, PayrollCorrectionService } from '../../../../services/payroll-correction.service';
import { CorrectionLineFilterComponent } from '../correction-line-filter/correction-line-filter.component';
import { CorrectionNotesDialogComponent } from '../correction-notes-dialog/correction-notes-dialog.component';
import {
  CORRECTION_LINE_COLUMNS,
  groupLineIdsByDataType,
  notifyCorrectionResults,
  toCorrectionLine,
} from '../correction-lines';

/** Denied / good-to-pay lines not yet sent to NAV that can be reprocessed. */
@Component({
  selector: 'ppl-reprocess',
  standalone: true,
  imports: [CommonModule, ListTableComponent, CorrectionLineFilterComponent, CorrectionNotesDialogComponent],
  templateUrl: './reprocess.component.html',
  styleUrl: './reprocess.component.css'
})
export class ReprocessComponent implements OnInit {
  pageId: string = PAGE_CONSTANTS.STAFF_REPROCESS;
  private readonly REPROCESS_BUTTON_ID = 'Reprocess';
  private readonly PAGE_NAME = 'payrollReprocessList';

  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;

  actionButtons: TableRowActionsModel = new TableRowActions({ view: false });
  programCode = '';
  lines: any[] = [];
  hasSearched = false;
  isReadOnly = true;
  showReprocessPopup = false;
  selectedLines: any[] = [];
  cols = CORRECTION_LINE_COLUMNS;
  private appliedFilters: any[] = [];

  buttons = [
    {
      button_id: this.REPROCESS_BUTTON_ID,
      button_label: 'Reprocess',
      buttonAction: 'reprocessLines',
      className: 'primary-btn-color'
    },
  ];

  constructor(
    private payrollCorrectionService: PayrollCorrectionService,
    private notificationService: NotificationService,
    private userService: UserService,
    private allowListStore: AllowListAuthStore
  ) { }

  ngOnInit(): void {
    this.programCode = this.userService.getSelectedProgram()?.program_code ?? '';
    this.isReadOnly = !this.allowListStore.isButtonAllowed(this.pageId, this.REPROCESS_BUTTON_ID);
  }

  /** Nothing is loaded until the user searches; the full list grows too large to load by default. */
  onSearch(filters: any[]) {
    this.appliedFilters = filters;
    this.hasSearched = true;
    this.reload();
  }

  onClear() {
    this.appliedFilters = [];
    this.hasSearched = false;
    this.lines = [];
  }

  onPageLoad(event: any) {
    this.loadLines(event.startingPageNumber, event.rows ?? 20);
  }

  loadLines(startingPageNumber = 1, recordsPerPage = 20) {
    const payload = {
      pageName: this.PAGE_NAME,
      programCode: this.programCode,
      condition: 'and',
      recordsPerPage,
      startingPageNumber,
      searchParam: this.appliedFilters,
      orderByColumn: ['providerId'],
      orderBy: ['desc'],
    };

    this.payrollCorrectionService.getListPageData(payload).subscribe((data: any) => {
      const records = (data?.responsedata ?? []).map(toCorrectionLine);
      this.lines = startingPageNumber > 1 ? [...this.lines, ...records] : records;
    });
  }

  gridAction(event: any) {
    if (event.button.buttonAction !== 'reprocessLines') return;
    this.selectedLines = event.selectedCheckBox ?? [];
    this.showReprocessPopup = true;
  }

  submitReprocess(notes: string) {
    const requests = Array.from(groupLineIdsByDataType(this.selectedLines), ([dataType, ids]) =>
      this.payrollCorrectionService.reprocess(this.programCode, dataType, notes, ids)
    );
    forkJoin(requests).subscribe((responses: any[]) => {
      this.showReprocessPopup = false;
      notifyCorrectionResults(this.notificationService, responses.flatMap(correctionResults), 'reprocessed');
      this.reload();
    });
  }

  /** Clear the grid's selection so reprocessed lines aren't sent again, then reload from page 1. */
  private reload() {
    this.selectedLines = [];
    if (this.childComponent?.selectedCheckBoxValue) {
      this.childComponent.selectedCheckBoxValue = [];
    }
    this.loadLines(1, this.childComponent?.recordsPerPage ?? 20);
  }
}
